import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardForm,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

/**
 * Account-page "Change email" panel. Requesting a change does NOT swap the login identifier — it emails a
 * confirm link to the NEW address; users.email changes only when that link is confirmed (POST-on-load by
 * /email-change/confirm). Until then the current address stays active, so the user keeps signing in normally.
 */
export function ChangeEmailSection({ currentEmail }: { currentEmail: string }) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set to the address we emailed the confirm link to, after a successful request → swap the form for a notice.
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await passportApi.auth.requestEmailChange(newEmail, password);
      setSentTo(newEmail.trim());
      setPassword("");
      toast.success("Verification email sent.");
    } catch (err) {
      // 400 same_email / 401 invalid_password / 409 email_taken / 502 email_send_failed — server body in message.
      toast.error(err instanceof Error ? err.message : "Failed to request email change");
    } finally {
      setSubmitting(false);
    }
  }

  if (sentTo) {
    return (
      <Card className="mt-4">
        <CardHeader className="space-y-1">
          <CardTitle className="text-lg font-semibold">Change email</CardTitle>
          <CardDescription>
            We sent a confirmation link to <span className="font-medium">{sentTo}</span>. Your email stays{" "}
            <span className="font-medium">{currentEmail}</span> until you open that link and confirm.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setSentTo(null);
              setNewEmail("");
            }}
          >
            Change a different address
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg font-semibold">Change email</CardTitle>
        <CardDescription>
          Current: <span className="font-medium">{currentEmail}</span>. We'll send a confirmation link to the new
          address — your email won't change until you confirm it.
        </CardDescription>
      </CardHeader>
      <CardForm onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="new_email">New email</Label>
            <Input
              id="new_email"
              type="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="current_password">Current password</Label>
            <Input
              id="current_password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Sending…" : "Send confirmation link"}
          </Button>
        </CardFooter>
      </CardForm>
    </Card>
  );
}
